import { useQuery, useMutation } from "@apollo/client";
import SelectTeam from "@components/account/Teams/SelectTeam";
import TeamUserDropdown from "@components/layout/header/TeamUserDropdown";
import {
  faArrowCircleRight,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TRANSFER_NOTES } from "@graphql/mutations/TransferNotes";
import getPatientsQuery from "@graphql/queries/GetPatients";
import { useTeam, useAuth, useNoteMultiselectContext } from "@hooks";
import Twemoji from "@src/Twemoji";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import { useState } from "react";

import { alert } from "../Alert";
import { ConfirmModal } from "./PopupModals";
import { evictNoteCache, truncate } from "../../../utils";
import NoteCreatedAt from "../../notebook/note/NoteCreatedAt";

export function TransferNotesModal({
  notes,
  shouldShowTransferModal,
  setShouldShowTransferModal,
  wasAutoPrompted = false,
  refetch,
  bypassContext = false,
}) {
  // filter null notes passed in
  notes = notes.filter(Boolean);
  // Get multiselect context
  const {
    selectedNoteUuids: selectedNoteUuidsFromContext,
    resetSelectedNotesList,
    numPatientNotes,
  } = useNoteMultiselectContext();

  const selectedNoteUuids = bypassContext
    ? notes.map((note) => note.uuid)
    : Object.keys(selectedNoteUuidsFromContext);

  const { currentTeam } = useTeam();
  const { userUuid } = useAuth();
  const { transferNewNotes } = useFlags();

  const [newUser, setNewUser] = useState(userUuid);
  const [newTeam, setNewTeam] = useState(currentTeam);
  const [applyCustomDefaults, setApplyCustomDefaults] =
    useState(transferNewNotes);

  // Query patients to possibly disable transfer if patients are still loading
  // As you cannot transfer patient notes
  const { loading: patientsLoading } = useQuery(getPatientsQuery, {
    fetchPolicy: "network-only",
  });
  const [transferNotes, { loading }] = useMutation(TRANSFER_NOTES);

  const numSelectedNotes =
    (selectedNoteUuids && selectedNoteUuids.length) || 0;

  const handleUserChange = (_, value) => {
    setNewUser(value);
  };
  const showApplyCustomDefaults =
    notes.filter(
      (note) =>
        selectedNoteUuids?.includes(note?.uuid) &&
        ["medical_record", "multipet", "packmode"].includes(
          note.jobType,
        ),
    ).length > 0;

  const confirmAction = async () => {
    if (numPatientNotes > 0) {
      alert(
        "error",
        "Unsuccessful Note Transfer: You can't transfer notes associated with one of your patients.",
      );
      return;
    }
    try {
      const { data } = await transferNotes({
        variables: {
          noteUuids: selectedNoteUuids,
          newTeamUuid: newTeam && newTeam.uuid,
          newOwnerUuid: newUser,
          applyCustomDefaults,
        },
        update: evictNoteCache,
      });

      if (
        data.transferNoteOwnership.failedNotes &&
        data.transferNoteOwnership.failedNotes.length
      ) {
        alert(
          "error",
          "Some notes failed to transfer. Are you sure you are the owner of all the notes you are transferring?",
        );
        mixpanel.track(
          `Failed to transfer note${
            selectedNoteUuids.length > 1 ? "s" : ""
          }`,
          {
            transferredNoteUuids: selectedNoteUuids,
            failedNotes: data.transferNoteOwnership.failedNotes,
            newNoteOwner: newUser,
            newNoteTeam: newTeam && newTeam.uuid,
            previousNoteOwner: localStorage.getItem("user_uuid"),
          },
        );
      } else {
        alert(
          "success",
          `Note${
            selectedNoteUuids.length > 1 ? "s" : ""
          } transferred successfully!`,
        );
        mixpanel.track("Transferred Note", {
          transferredNoteUuids: selectedNoteUuids,
          newNoteOwner: newUser,
          newNoteTeam: newTeam && newTeam.uuid,
          previousNoteOwner: localStorage.getItem("user_uuid"),
        });
      }
      if (refetch) refetch();
      resetSelectedNotesList();
      setShouldShowTransferModal(false);
    } catch (error) {
      alert(
        "error",
        "Unsuccessful Note Transfer. Please try again later. If the issue persists, please contact support.",
      );
      mixpanel.track("Transfer Note Error", {
        transferredNoteUuids: selectedNoteUuids,
        newNoteOwner: newUser,
        previousNoteOwner: localStorage.getItem("user_uuid"),
        transferNoteError: error,
      });
    }
  };

  return (
    <ConfirmModal
      shouldShow={shouldShowTransferModal}
      hideModal={() => setShouldShowTransferModal(false)}
      title={
        numSelectedNotes > 1
          ? "Transfer these notes?"
          : "Transfer this note?"
      }
      confirmText="Transfer"
      confirmAction={confirmAction}
      cancelAction={() => setShouldShowTransferModal(false)}
      disabled={loading || patientsLoading}
    >
      <h5>
        Which team member would you like to{" "}
        <strong>
          transfer{" "}
          {numSelectedNotes > 1
            ? `these ${numSelectedNotes} notes`
            : "this note"}
        </strong>{" "}
        to?
      </h5>
      <div className="flex flex-row items-center space-x-2 mt-4">
        <div className="flex-1">
          {notes &&
            notes.map((note) => {
              if (selectedNoteUuids?.includes(note?.uuid)) {
                return (
                  <h5
                    key={note.uuid}
                    className="text-sm py-1 border p-2 rounded-lg dark:border-gray-700/50 bg-gray-50 dark:bg-gray-700 my-1"
                  >
                    <Twemoji emoji={"📝"} />{" "}
                    <span className="font-medium">
                      {note.patient ? note.patient.name : ""}
                    </span>{" "}
                    <span>
                      <span className="font-medium">
                        {truncate(note.title, 40) || "Untitled Note"}
                      </span>{" "}
                      <NoteCreatedAt
                        createdAt={note.createdAt}
                        size="sm"
                      />
                    </span>
                  </h5>
                );
              }
              return null;
            })}
        </div>
        <FontAwesomeIcon
          className="dark:text-gray-700"
          icon={faArrowCircleRight}
          size="2x"
        />
        <div className="flex-1">
          <SelectTeam
            currentTeam={newTeam}
            setCurrentTeam={setNewTeam}
          />
          <TeamUserDropdown
            propHandleChange={handleUserChange}
            currentTeam={newTeam}
            currentUser={newUser}
            setCurrentUser={setNewUser}
          />
        </div>
      </div>
      {showApplyCustomDefaults && (
        <div>
          <label className="flex items-center mt-4">
            <div className="flex flex-row items-center justify-center relative">
              <input
                checked={applyCustomDefaults}
                onChange={(e) =>
                  setApplyCustomDefaults(e.target.checked)
                }
                type="checkbox"
                className="appearance-none rounded-md bg-white dark:bg-gray-700 border-2 border-indigo-500 checked:bg-indigo-500 dark:checked:bg-indigo-500 checked:border-transparent h-6 w-6 cursor-pointer transition-all"
              />

              {applyCustomDefaults && (
                <FontAwesomeIcon
                  icon={faCheck}
                  className="text-white absolute pointer-events-none"
                />
              )}
            </div>

            <div className="text-xs leading-none ml-2">
              Apply their custom defaults to the note*
              <br />
              <small>
                *This will overwrite any edits made to the note
              </small>
            </div>
          </label>
        </div>
      )}

      {wasAutoPrompted && (
        <div className="text-xs mt-4">
          If you don't want to be prompted to transfer newly created
          notes you can disable this feature in your Account Settings
          under Teams.
        </div>
      )}
    </ConfirmModal>
  );
}
